import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Cache } from 'aws-amplify';
import { FilterOptionsService, FilterParams } from '../../../../../core/filter-options/filter-options.service';
import { SelectOption, SearchableSelectComponent } from '../searchable-select/searchable-select.component';

@Component({
	selector: 'kt-hierarchical-filters',
	templateUrl: './hierarchical-filters.component.html',
	styleUrls: ['./hierarchical-filters.component.scss']
})
export class HierarchicalFiltersComponent implements OnInit, OnChanges {
	@Input() role: string = 'sadmin';
	@Input() showDistributor: boolean = true;
	@Input() showMarket: boolean = true;
	@Input() showClient: boolean = true;
	@Input() showCampus: boolean = true;
	@Input() showMachine: boolean = false;
	@Input() disabled: boolean = false;
	@Input() multiple: boolean = true; // Default to true for backward compatibility
	@Input() initialValues?: {
		distributorIds?: (string | number)[];
		marketIds?: (string | number)[];
		clientIds?: (string | number)[]
		campusIds?: (string | number)[];
		machineIds?: (string | number)[];
	};
	@Input() includeAllOption: boolean = false;
	@Input() retailOnly: boolean = false;
	// Only clients, campuses, and machines with retail equipment (card readers, cold brew fridges),
	// as the Retail Analytics page counts them. Separate from retailOnly (the machine's mode).
	@Input() retailEquipmentOnly: boolean = false;

	@Output() filterChange = new EventEmitter<FilterParams>();

	// ViewChild references to searchable-select components
	@ViewChild('distributorSelect', { static: false }) distributorSelect?: SearchableSelectComponent;
	@ViewChild('marketSelect', { static: false }) marketSelect?: SearchableSelectComponent;
	@ViewChild('clientSelect', { static: false }) clientSelect?: SearchableSelectComponent;
	@ViewChild('campusSelect', { static: false }) campusSelect?: SearchableSelectComponent;
	@ViewChild('machineSelect', { static: false }) machineSelect?: SearchableSelectComponent;

	// Track which dropdown is currently open
	private currentlyOpenDropdown: SearchableSelectComponent | null = null;

	// Internal state
	selectedDistributors: (string | number)[] = [];
	selectedMarkets: (string | number)[] = [];
	selectedClients: (string | number)[] = [];
	selectedCampuses: (string | number)[] = [];
	selectedMachines: (string | number)[] = [];

	// Observable options
	distributorOptions$ = new BehaviorSubject<SelectOption[]>([]);
	marketOptions$ = new BehaviorSubject<SelectOption[]>([]);
	clientOptions$ = new BehaviorSubject<SelectOption[]>([]);
	campusOptions$ = new BehaviorSubject<SelectOption[]>([]);
	machineOptions$ = new BehaviorSubject<SelectOption[]>([]);

	// Loading states for each filter type
	loadingMarkets = false;
	loadingClients = false;
	loadingCampuses = false;
	loadingMachines = false;

	private user: any;
	// Base scope: role-based IDs that should always be preserved when clearing
	private baseScopeDistributorIds: (string | number)[] = [];
	private baseScopeMarketIds: (string | number)[] = [];
	private baseScopeClientIds: (string | number)[] = [];

	constructor(
		private filterService: FilterOptionsService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.user = Cache.getItem('userAttr');
		this.initializeFilters();
		this.loadInitialOptions();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['initialValues'] && !changes['initialValues'].firstChange) {
			this.initializeFilters();
			this.loadInitialOptions();
		}

		if (changes['role'] && !changes['role'].firstChange) {
			// Re-determine base scope when role changes
			this.determineBaseScope();
			this.loadInitialOptions();
		}
	}

	get hasDistributor(): boolean {
		return this.selectedDistributors.length > 0 && this.selectedDistributors[0] !== null;
	}

	get hasClient(): boolean {
		return this.selectedClients.length > 0 && this.selectedClients[0] !== null;
	}

	get hasCampus(): boolean {
		return this.selectedCampuses.length > 0 && this.selectedCampuses[0] !== null;
	}

	get hasAnySelection(): boolean {
		return this.hasDistributor || this.selectedMarkets.length > 0 || this.hasClient ||
		       this.selectedCampuses.length > 0 || this.selectedMachines.length > 0;
	}

	get hasVisibleFilters(): boolean {
		return this.showDistributor || this.showMarket || this.showClient ||
		       this.showCampus || this.showMachine;
	}

	private initializeFilters(): void {
		// Determine base scope (role-based IDs that should be preserved)
		this.determineBaseScope();

		if (this.initialValues) {
			this.selectedDistributors = this.initialValues.distributorIds || [];
			this.selectedMarkets = this.initialValues.marketIds || [];
			this.selectedClients = this.initialValues.clientIds || [];
			this.selectedCampuses = this.initialValues.campusIds || [];
			this.selectedMachines = this.initialValues.machineIds || [];
		} else {
			// Initialize from base scope
			this.selectedDistributors = [...this.baseScopeDistributorIds];
			this.selectedMarkets = [...this.baseScopeMarketIds];
			this.selectedClients = [...this.baseScopeClientIds];
			// Campus ID is not preserved in base scope (it's a more specific filter)
			if (this.user && this.user['custom:campus_id']) {
				this.selectedCampuses = [this.user['custom:campus_id']];
			}
		}
	}

	/**
	 * Determines the base scope (role-based IDs) that should always be preserved.
	 * This is the minimum scope a user should have based on their role.
	 */
	private determineBaseScope(): void {
		this.baseScopeDistributorIds = [];
		this.baseScopeMarketIds = [];
		this.baseScopeClientIds = [];

		if (!this.user) {
			return;
		}

		// For distiadmin: preserve distributor ID
		if (this.role === 'distiadmin' && this.user['custom:disti_id']) {
			this.baseScopeDistributorIds = [this.user['custom:disti_id']];
		}

		// For marketadmin: preserve market ID
		if (this.role === 'marketadmin' && this.user['custom:market_id']) {
			this.baseScopeMarketIds = [this.user['custom:market_id']];
		}

		// For marketadmin: also preserve distributor ID for create flows
		if (this.role === 'marketadmin' && this.user['custom:disti_id']) {
			this.baseScopeDistributorIds = [this.user['custom:disti_id']];
		}

		// For clientadmin: preserve client ID
		if (this.role === 'clientadmin' && this.user['custom:client_id']) {
			this.baseScopeClientIds = [this.user['custom:client_id']];
		}
	}

	private loadInitialOptions(): void {
		// Load distributors if role allows
		if (this.showDistributor && this.role === 'sadmin') {
			this.loadDistributors();
		}

		// For distiadmin, auto-load markets and clients if distributor is set
		if (this.role === 'distiadmin' && this.user && this.user['custom:disti_id']) {
			if (!this.hasDistributor) {
				this.selectedDistributors = [this.user['custom:disti_id']];
			}
			if (this.showMarket) {
				this.loadMarkets();
			}
			if (this.showClient) {
				this.loadClients();
			}
		}

		// For marketadmin, auto-load clients and campuses if market is set
		if (this.role === 'marketadmin' && this.user && this.user['custom:market_id']) {
			if (this.selectedMarkets.length === 0) {
				this.selectedMarkets = [this.user['custom:market_id']];
			}
			if (this.showClient) {
				this.loadClients();
			}
			if (this.showCampus && this.user['custom:client_id']) {
				if (!this.hasClient) {
					this.selectedClients = [this.user['custom:client_id']];
				}
				this.loadCampuses();
			}
		}

		// For clientadmin, auto-load campuses if client is set
		if (this.role === 'clientadmin' && this.user && this.user['custom:client_id']) {
			if (!this.hasClient) {
				this.selectedClients = [this.user['custom:client_id']];
			}
			if (this.showCampus) {
				this.loadCampuses();
			}
		}

		// Load markets only if distributor is selected and showMarket is true
		if (this.showMarket && this.hasDistributor) {
			this.loadMarkets();
		}

		// Load clients, campuses, and machines (they work independently)
		// Skip loading clients if showClient is false or role is clientadmin (they already have their client ID)
		// For marketadmin, clients are loaded above if market is set
		if (this.showClient && this.role !== 'clientadmin' && this.role !== 'marketadmin') {
			this.loadClients();
		}
		// Skip loading campuses if showCampus is false or role is campusadmin (they already have their campus ID)
		if (this.showCampus && this.role !== 'campusadmin') {
			this.loadCampuses();
		}
		if (this.showMachine) {
			this.loadMachines();
		}

		this.emitFilterChange();
	}

	loadDistributors(): void {
		this.filterService.loadDistributors(this.role, this.includeAllOption)
			.subscribe(options => {
				this.distributorOptions$.next(options);
				this.cdr.detectChanges();
			});
	}

	loadMarkets(): void {
		this.loadingMarkets = true;
		this.marketOptions$.next([]); // Clear old options immediately
		const distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
		this.filterService.loadMarkets(distributorIds, this.includeAllOption)
			.subscribe({
				next: (options) => {
					this.marketOptions$.next(options);
					this.loadingMarkets = false;
					this.cdr.detectChanges();
				},
				error: () => {
					this.loadingMarkets = false;
					this.cdr.detectChanges();
				}
			});
	}

	loadClients(): void {
		this.loadingClients = true;
		this.clientOptions$.next([]); // Clear old options immediately
		// For marketadmin, always use their market scope
		let distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
		let marketIds = this.selectedMarkets.length > 0 ? this.selectedMarkets : null;

		// If marketadmin and no markets selected, use base scope market
		if (this.role === 'marketadmin' && (!marketIds || marketIds.length === 0) && this.baseScopeMarketIds.length > 0) {
			marketIds = this.baseScopeMarketIds;
		}

		this.filterService.loadClients(distributorIds, marketIds, this.includeAllOption, this.retailOnly, this.retailEquipmentOnly)
			.subscribe({
				next: (options) => {
					this.clientOptions$.next(options);
					this.loadingClients = false;
					this.cdr.detectChanges();
				},
				error: () => {
					this.loadingClients = false;
					this.cdr.detectChanges();
				}
			});
	}

	loadCampuses(): void {
		this.loadingCampuses = true;
		this.campusOptions$.next([]); // Clear old options immediately
		const clientIds = this.selectedClients.length > 0 ? this.selectedClients : null;
		const distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
		const marketIds = this.selectedMarkets.length > 0 ? this.selectedMarkets : null;
		this.filterService.loadCampuses(clientIds, distributorIds, marketIds, this.includeAllOption, this.retailOnly, this.retailEquipmentOnly)
			.subscribe({
				next: (options) => {
					this.campusOptions$.next(options);
					this.loadingCampuses = false;
					this.cdr.detectChanges();
				},
				error: () => {
					this.loadingCampuses = false;
					this.cdr.detectChanges();
				}
			});
	}

	loadMachines(): void {
		this.loadingMachines = true;
		this.machineOptions$.next([]); // Clear old options immediately
		const campusIds = this.selectedCampuses.length > 0 ? this.selectedCampuses : null;
		const clientIds = this.selectedClients.length > 0 ? this.selectedClients : null;
		const distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
		const marketIds = this.selectedMarkets.length > 0 ? this.selectedMarkets : null;
		this.filterService.loadMachines(campusIds, clientIds, distributorIds, marketIds, this.includeAllOption, this.retailEquipmentOnly)
			.subscribe({
				next: (options) => {
					this.machineOptions$.next(options);
					this.loadingMachines = false;
					this.cdr.detectChanges();
				},
				error: () => {
					this.loadingMachines = false;
					this.cdr.detectChanges();
				}
			});
	}

	onDistributorChange(values: (string | number) | (string | number)[] | null): void {
		// Normalize input: convert single value to array, handle null
		const normalizedValues = this.normalizeToArray(values);
		this.selectedDistributors = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);

		// Reset dependent filters
		this.selectedMarkets = [];
		this.selectedClients = [];
		this.selectedCampuses = [];
		this.selectedMachines = [];

		// Reload markets only if distributor is selected
		if (this.selectedDistributors.length > 0 && this.selectedDistributors[0] !== null) {
			this.loadMarkets();
		} else {
			this.marketOptions$.next([]);
		}

		// Always reload clients, campuses, and machines (they work independently)
		this.loadClients();
		this.loadCampuses();
		this.loadMachines();

		this.emitFilterChange();
	}

	onMarketChange(values: (string | number) | (string | number)[] | null): void {
		// Normalize input: convert single value to array, handle null
		const normalizedValues = this.normalizeToArray(values);
		this.selectedMarkets = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);

		// Reset dependent filters
		this.selectedClients = [];
		this.selectedCampuses = [];
		this.selectedMachines = [];

		// Always reload clients, campuses, and machines (they work independently)
		this.loadClients();
		this.loadCampuses();
		this.loadMachines();

		this.emitFilterChange();
	}

	onClientChange(values: (string | number) | (string | number)[] | null): void {
		// Normalize input: convert single value to array, handle null
		const normalizedValues = this.normalizeToArray(values);
		this.selectedClients = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);

		// Reset dependent filters
		this.selectedCampuses = [];
		this.selectedMachines = [];

		// Always reload campuses and machines (they work independently)
		this.loadCampuses();
		this.loadMachines();

		this.emitFilterChange();
	}

	onCampusChange(values: (string | number) | (string | number)[] | null): void {
		// Normalize input: convert single value to array, handle null
		const normalizedValues = this.normalizeToArray(values);
		this.selectedCampuses = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);

		// Reset dependent filters
		this.selectedMachines = [];

		// Always reload machines (they work independently)
		this.loadMachines();

		this.emitFilterChange();
	}

	onMachineChange(values: (string | number) | (string | number)[] | null): void {
		// Normalize input: convert single value to array, handle null
		const normalizedValues = this.normalizeToArray(values);
		this.selectedMachines = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);
		this.emitFilterChange();
	}

	/**
	 * Normalize a value that could be a single value, array, or null into an array
	 */
	private normalizeToArray(value: (string | number) | (string | number)[] | null): (string | number)[] {
		if (value === null || value === undefined) {
			return [];
		}
		if (Array.isArray(value)) {
			return value;
		}
		return [value];
	}

	private emitFilterChange(): void {
		const params: FilterParams = {
			disti_id: this.multiple
				? (this.selectedDistributors.length > 0 ? this.selectedDistributors : null)
				: (this.selectedDistributors.length > 0 ? (this.selectedDistributors[0] || null) : null),
			market_id: this.multiple
				? (this.selectedMarkets.length > 0 ? this.selectedMarkets : null)
				: (this.selectedMarkets.length > 0 ? (this.selectedMarkets[0] || null) : null),
			client_id: this.multiple
				? (this.selectedClients.length > 0 ? this.selectedClients : null)
				: (this.selectedClients.length > 0 ? (this.selectedClients[0] || null) : null),
			campus_id: this.multiple
				? (this.selectedCampuses.length > 0 ? this.selectedCampuses : null)
				: (this.selectedCampuses.length > 0 ? (this.selectedCampuses[0] || null) : null),
			machine_id: this.multiple
				? (this.selectedMachines.length > 0 ? this.selectedMachines : null)
				: (this.selectedMachines.length > 0 ? (this.selectedMachines[0] || null) : null)
		};
		this.filterChange.emit(params);
	}

	// Public method to get current filter params
	getFilterParams(): FilterParams {
		return {
			disti_id: this.multiple
				? (this.selectedDistributors.length > 0 ? this.selectedDistributors : null)
				: (this.selectedDistributors.length > 0 ? (this.selectedDistributors[0] || null) : null),
			market_id: this.multiple
				? (this.selectedMarkets.length > 0 ? this.selectedMarkets : null)
				: (this.selectedMarkets.length > 0 ? (this.selectedMarkets[0] || null) : null),
			client_id: this.multiple
				? (this.selectedClients.length > 0 ? this.selectedClients : null)
				: (this.selectedClients.length > 0 ? (this.selectedClients[0] || null) : null),
			campus_id: this.multiple
				? (this.selectedCampuses.length > 0 ? this.selectedCampuses : null)
				: (this.selectedCampuses.length > 0 ? (this.selectedCampuses[0] || null) : null),
			machine_id: this.multiple
				? (this.selectedMachines.length > 0 ? this.selectedMachines : null)
				: (this.selectedMachines.length > 0 ? (this.selectedMachines[0] || null) : null)
		};
	}

	clearScope(): void {
		// Reset to base scope (role-based IDs that should always be preserved)
		this.selectedDistributors = [...this.baseScopeDistributorIds];
		this.selectedMarkets = [...this.baseScopeMarketIds];
		this.selectedClients = [...this.baseScopeClientIds];
		this.selectedCampuses = [];
		this.selectedMachines = [];

		// Reset dependent options
		this.marketOptions$.next([]);
		this.clientOptions$.next([]);
		this.campusOptions$.next([]);
		this.machineOptions$.next([]);

		// Reload options based on base scope
		if (this.selectedDistributors.length > 0) {
			this.loadMarkets();
		}
		// For marketadmin, load clients using their market scope
		if (this.role === 'marketadmin' && this.baseScopeMarketIds.length > 0) {
			this.loadClients();
		} else {
			// Always reload clients, campuses, and machines (they work independently and can handle null params)
			this.loadClients();
		}
		this.loadCampuses();
		this.loadMachines();

		this.emitFilterChange();
	}

	// Display value functions for each filter type
	getDistributorDisplay = (selected: SelectOption[]): string => {
		if (selected.length === 0) return 'All Distributors';
		if (selected.length <= 3) return selected.map(s => s.label).join(', ');
		return `${selected.length} selected`;
	}

	getMarketDisplay = (selected: SelectOption[]): string => {
		if (selected.length === 0) return 'All Markets';
		if (selected.length <= 3) return selected.map(s => s.label).join(', ');
		return `${selected.length} selected`;
	}

	getClientDisplay = (selected: SelectOption[]): string => {
		if (selected.length === 0) return 'All Clients';
		if (selected.length <= 3) return selected.map(s => s.label).join(', ');
		return `${selected.length} selected`;
	}

	getCampusDisplay = (selected: SelectOption[]): string => {
		if (selected.length === 0) return 'All Campuses';
		if (selected.length <= 3) return selected.map(s => s.label).join(', ');
		return `${selected.length} selected`;
	}

	getMachineDisplay = (selected: SelectOption[]): string => {
		if (selected.length === 0) return 'All Machines';
		if (selected.length <= 3) return selected.map(s => s.label).join(', ');
		return `${selected.length} selected`;
	}

	/**
	 * Handle dropdown open event - close any other open dropdowns
	 */
	onDropdownOpen(openedDropdown: SearchableSelectComponent): void {
		// If there's another dropdown open, close it and submit its changes
		if (this.currentlyOpenDropdown && this.currentlyOpenDropdown !== openedDropdown) {
			this.currentlyOpenDropdown.closeDropdownProgrammatically();
			this.currentlyOpenDropdown = null; // Clear tracking immediately
		}
		// Track the newly opened dropdown
		this.currentlyOpenDropdown = openedDropdown;
	}

	/**
	 * Handle dropdown close event - clear tracking
	 */
	onDropdownClose(): void {
		// Clear tracking when dropdown closes
		this.currentlyOpenDropdown = null;
	}

	/**
	 * Close all dropdowns programmatically
	 */
	closeAllDropdowns(): void {
		const dropdowns = [
			this.distributorSelect,
			this.marketSelect,
			this.clientSelect,
			this.campusSelect,
			this.machineSelect
		].filter(d => d !== undefined) as SearchableSelectComponent[];

		dropdowns.forEach(dropdown => {
			if (dropdown) {
				dropdown.closeDropdownProgrammatically();
			}
		});

		this.currentlyOpenDropdown = null;
	}
}


